import React, { createContext, useReducer, useContext } from 'react';
import { IUsefulLinkResponse } from '../../../model/base/response/useful-links-response';
import { ICategoryLinkResponse } from '../../../model/base/response/category-link-response';

interface UsefulLinksState {
  links: IUsefulLinkResponse[];
  categories: ICategoryLinkResponse[];
  loading: boolean;
  error: string | null;
}

type UsefulLinksAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_LINKS'; payload: IUsefulLinkResponse[] }
  | { type: 'SET_CATEGORIES'; payload: ICategoryLinkResponse[] }
  | { type: 'SET_ERROR'; payload: string | null };

const initialState: UsefulLinksState = {
  links: [],
  categories: [],
  loading: false,
  error: null,
};

const UsefulLinksStateContext = createContext<UsefulLinksState | undefined>(undefined);
const UsefulLinksDispatchContext = createContext<React.Dispatch<UsefulLinksAction> | undefined>(undefined);

function usefulLinksReducer(state: UsefulLinksState, action: UsefulLinksAction): UsefulLinksState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_LINKS':
      return { ...state, links: action.payload, loading: false };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

export function UsefulLinksProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(usefulLinksReducer, initialState);

  return (
    <UsefulLinksStateContext.Provider value={state}>
      <UsefulLinksDispatchContext.Provider value={dispatch}>
        {children}
      </UsefulLinksDispatchContext.Provider>
    </UsefulLinksStateContext.Provider>
  );
}

export function useUsefulLinksState() {
  const context = useContext(UsefulLinksStateContext);
  if (context === undefined) {
    throw new Error('useUsefulLinksState must be used within a UsefulLinksProvider');
  }
  return context;
}

export function useUsefulLinksDispatch() {
  const context = useContext(UsefulLinksDispatchContext);
  if (context === undefined) {
    throw new Error('useUsefulLinksDispatch must be used within a UsefulLinksProvider');
  }
  return context;
}
